import { z } from 'zod';
import { objectIdSchema, priceSchema } from '../common.js';
/**
 * Narx o'zgarishlari tarixi (TZ 3.9).
 *
 * Nima uchun alohida kolleksiya: narx yiliga o'nlab marta o'zgaradi va
 * mahsulot hujjati ichidagi massiv cheksiz o'sardi (ISH-REJA §4.3).
 * Bundan tashqari tarix «kim, qachon, nimadan nimaga» savoliga javob beradi —
 * u audit yozuvi, kontent emas.
 */
export const priceHistorySchema = z.object({
    _id: objectIdSchema,
    productId: objectIdSchema,
    variantId: z.string().max(40),
    /** Snapshot: mahsulot keyin o'chsa ham tarix o'qilishi kerak. */
    productTitle: z.string().max(300),
    variantTitle: z.string().max(300),
    sku: z.string().max(60).nullable().default(null),
    oldPrice: priceSchema,
    newPrice: priceSchema,
    /** Nima sabab o'zgardi: qo'lda tahrir, ommaviy yangilash, XLSX import. */
    source: z.enum(['manual', 'bulk', 'import', 'migration']).default('manual'),
    userId: objectIdSchema.nullable().default(null),
    userEmail: z.string().max(200).default(''),
    /** Bitta ommaviy amaldagi barcha yozuvlar shu identifikator bilan bog'lanadi. */
    batchId: z.string().max(40).nullable().default(null),
    note: z.string().max(500).default(''),
    createdAt: z.coerce.date(),
});
// ─── Ommaviy yangilash ───────────────────────────────────────────────────────
/**
 * Narxlarni ommaviy o'zgartirish (TZ 3.9).
 *
 * `percent` — foizga oshirish/kamaytirish, `absolute` — qo'shish/ayirish,
 * `set` — aniq qiymat. `null` narxli («Narxni so'rang») komplektatsiyalar
 * FOIZ va QO'SHISH amallarida chetlab o'tiladi: `null` dan foiz olib
 * bo'lmaydi va uni 0 deb hisoblash noto'g'ri bo'lardi.
 */
export const bulkPriceOperationSchema = z.enum(['percent', 'absolute', 'set']);
export const bulkPriceUpdateSchema = z
    .object({
    operation: bulkPriceOperationSchema,
    /** `percent` uchun foiz (−90…1000), boshqalar uchun so'm. */
    value: z.number(),
    /** Nishon: butun katalog, kategoriya, brend yoki tanlangan mahsulotlar. */
    scope: z.enum(['all', 'category', 'brand', 'products']),
    categoryId: objectIdSchema.optional(),
    brandId: objectIdSchema.optional(),
    productIds: z.array(objectIdSchema).max(500).optional(),
    /** Natijani 1000 so'mgacha yaxlitlash — narxlar chiroyli ko'rinsin. */
    roundTo: z.number().int().min(1).max(1_000_000).default(1000),
    note: z.string().trim().max(500).default(''),
    /** `true` — faqat oldindan ko'rish, bazaga yozilmaydi. */
    dryRun: z.boolean().default(false),
})
    .refine((v) => v.scope !== 'category' || Boolean(v.categoryId), {
    message: 'Kategoriya tanlanmagan',
    path: ['categoryId'],
})
    .refine((v) => v.scope !== 'brand' || Boolean(v.brandId), {
    message: 'Brend tanlanmagan',
    path: ['brandId'],
})
    .refine((v) => v.scope !== 'products' || (v.productIds?.length ?? 0) > 0, {
    message: 'Mahsulotlar tanlanmagan',
    path: ['productIds'],
})
    .refine((v) => v.operation !== 'percent' || (v.value >= -90 && v.value <= 1000), {
    message: 'Foiz −90 dan 1000 gacha bo‘lishi kerak',
    path: ['value'],
})
    .refine((v) => v.operation !== 'set' || (Number.isInteger(v.value) && v.value >= 0), {
    message: 'Narx manfiy bo‘lmagan butun son bo‘lishi kerak',
    path: ['value'],
});
export const listPriceHistoryQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(100).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    productId: objectIdSchema.optional(),
    batchId: z.string().max(40).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
/**
 * Ommaviy amalda yangi narxni hisoblaydi.
 *
 * `null` («Narxni so'rang») — `set` dan boshqa amallarda O'ZGARISHSIZ qoladi:
 * mavjud bo'lmagan narxdan foiz olib bo'lmaydi.
 */
export function applyPriceOperation(current, operation, value, roundTo) {
    if (operation === 'set')
        return roundPrice(Math.max(0, value), roundTo);
    if (current === null)
        return null;
    const raw = operation === 'percent' ? current * (1 + value / 100) : current + value;
    return roundPrice(Math.max(0, raw), roundTo);
}
export function roundPrice(value, roundTo) {
    if (roundTo <= 1)
        return Math.round(value);
    return Math.round(value / roundTo) * roundTo;
}
//# sourceMappingURL=price.js.map